// The Console's Logs tab reads this store. It is an in-memory ring of the most
// recent log lines for this page load. Nothing is written to disk.
//
// The widget sink in ./logger is the only writer. React reads it with
// useSyncExternalStore (see useLogStore).

export type LogLevelName = 'debug' | 'info' | 'warning' | 'error'

export interface LogEntry {
  /** Monotonic per page load. Doubles as the React key. */
  readonly seq: number
  readonly timestamp: number
  readonly level: LogLevelName
  /** Caller-chosen origin, e.g. "segmentation" or "margin-tool". */
  readonly origin: string
  /** Plain text: what search, Export and DevTools see. */
  readonly text: string
  /** Trusted markup from an html`` message, when there is one. */
  readonly html?: string
  /** Extra data passed with the message, minus any Error (see `stack`). */
  readonly properties?: Readonly<Record<string, unknown>>
  /** Stack of the Error passed with the message, if any. */
  readonly stack?: string
}

/** How each level reads on a row, in Export and in DevTools. */
export const LOG_LEVEL_LABEL: Record<LogLevelName, string> = {
  debug: 'DEBUG',
  info: 'INFO',
  warning: 'WARN',
  error: 'ERROR',
}

export const LOG_BUFFER_CAP = 500

type Listener = () => void

export interface UnseenCounts {
  readonly warning: number
  readonly error: number
}

/** The Debug switch, remembered for this tab only (like the Console height). */
export const LOG_DEBUG_KEY = 'orovia.console.logDebug'

let entries: readonly LogEntry[] = []
let nextSeq = 1
// null until first read, so module load never touches sessionStorage.
let debugEnabled: boolean | null = null
// Unseen WARN/ERROR lines for the collapsed handle's badge. Counted only while
// the Logs tab is not on screen; showing it zeroes them.
let logsVisible = false
let unseen: UnseenCounts = { warning: 0, error: 0 }
const listeners = new Set<Listener>()

function loadDebug(): boolean {
  try {
    return typeof window !== 'undefined' && window.sessionStorage.getItem(LOG_DEBUG_KEY) === 'true'
  } catch {
    return false
  }
}

function emit(): void {
  for (const l of listeners) l()
}

export const logStore = {
  getEntries(): readonly LogEntry[] {
    return entries
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },

  push(entry: Omit<LogEntry, 'seq'>): void {
    const next = [...entries, { ...entry, seq: nextSeq++ }]
    entries = next.length > LOG_BUFFER_CAP ? next.slice(next.length - LOG_BUFFER_CAP) : next
    if (!logsVisible && (entry.level === 'warning' || entry.level === 'error')) {
      unseen = { ...unseen, [entry.level]: unseen[entry.level] + 1 }
    }
    emit()
  },

  clear(): void {
    entries = []
    unseen = { warning: 0, error: 0 }
    emit()
  },

  getUnseen(): UnseenCounts {
    return unseen
  },

  /** Zero the unseen counts. The Console calls this when it opens, so the
   *  badge then counts only lines that land after that. */
  markSeen(): void {
    if (unseen.warning === 0 && unseen.error === 0) return
    unseen = { warning: 0, error: 0 }
    emit()
  },

  /** The Console tells the store whether the Logs tab is on screen. While it
   *  is, nothing counts as unseen. */
  setLogsVisible(visible: boolean): void {
    logsVisible = visible
    if (visible) logStore.markSeen()
  },

  /** The Debug switch. Off drops DEBUG records before they reach either
   *  destination; it is not a view filter. */
  isDebugEnabled(): boolean {
    if (debugEnabled == null) debugEnabled = loadDebug()
    return debugEnabled
  },

  setDebugEnabled(on: boolean): void {
    debugEnabled = on
    try {
      window.sessionStorage.setItem(LOG_DEBUG_KEY, String(on))
    } catch {
      // Storage blocked: the switch still works for this page load.
    }
    emit()
  },

  /** Back to a fresh page load: no lines, switch re-read from storage. */
  reset(): void {
    entries = []
    debugEnabled = null
    logsVisible = false
    unseen = { warning: 0, error: 0 }
    emit()
  },
}
